import { FileIcon } from '@commenda/icons';
import { FileBasic } from '@commenda/types/src/file/types';
import { classNames, cn, downloadFileFromLink } from '@commenda/utils';
import { fileIconMap } from '@commenda/utils/file-icon-map';
import { LegacyDocumentType } from '@prisma/client';
import { useCallback, useEffect, useState } from 'react';
import { Accept, ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { useToggle } from '../../hooks';
import { Button } from '../Button';
import { FeaturedIcon, Icons } from '../Icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { PreviewDownloadDialog } from '../PreviewDownloadDialog';
import { showNotification } from '../Toast';
import {
  formatBytesString,
  getErrorMessage,
  GetSignedUrlForPut,
  uploadFileToS3,
} from './utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';

export enum FileUploadStatus {
  UPLOADING,
  FAILED,
  SUCCESS,
}

export type SingleFileUploaderProps = {
  id?: string;
  file: FileBasic;
  setFile: React.Dispatch<React.SetStateAction<FileBasic | null>>;
  onFileChange?: (file: FileBasic) => void;
  // Nullable to match uploadFileToS3 and the `if (!uploadFileResponse)` branch below:
  // an override that throws instead leaves the field spinning with no error.
  onFileUpload?: (
    file: File,
    companyId: number | undefined,
    token: string | undefined,
    clerkToken?: string
  ) => Promise<{ fileUrl: string; fileName: string; downloadUrl: string } | null>;
  // Replaces the default `s3/signedUrlForPut` presign. Ignored when `onFileUpload` is set.
  getSignedUrlForPut?: GetSignedUrlForPut;
  documentType?: LegacyDocumentType;
  maxSize?: number;
  accept?: Accept;
  _className?: string;
  error?: string;
  setError?: React.Dispatch<React.SetStateAction<string>>;
  onError?: () => void;
  token?: string | undefined;
  disabled?: boolean;
  onSuccess?: () => void;
  onUploadStatusChange?: (status: FileUploadStatus | null) => void;
  message?: string;
  fileActions?: FileAction[];
  hideDefaultActions?: string[];
  companyId: number | undefined;
  clerkToken?: string;
};

interface IconProps {
  className: string;
}

type IconComponent = React.ComponentType<IconProps>;

type TLocalFile = {
  fileName: string;
  fileSize: number | null;
  error: {
    hasError: boolean;
    message: string | null;
  };
};

export enum FileActionKey {
  DELETE = 'delete',
  PREVIEW = 'preview',
  DOWNLOAD = 'download',
}

export type FileAction = {
  key: FileActionKey | string; // string is for custom actions and enables backward compatibility
  label: string;
  icon?: IconComponent;
  onClick?: () => void;
  order?: number;
};

const DEFAULT_ACTIONS: FileAction[] = [
  {
    key: FileActionKey.PREVIEW,
    label: 'Preview',
    icon: Icons.Eye,
    order: 997,
  },
  {
    key: FileActionKey.DOWNLOAD,
    label: 'Download',
    icon: Icons.Download01,
    order: 998,
  },
  {
    key: FileActionKey.DELETE,
    label: 'Delete',
    icon: Icons.Trash01,
    order: 999,
  },
];

const DefaultActionsMenu = ({ actions }: { actions: FileAction[] }) => (
  <Popover>
    <PopoverContent className="w-[180px] p-0" align="end">
      <div className="py-2">
        {actions.map((action) => (
          <div
            key={action.key}
            className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
            onClick={(e) => {
              e.stopPropagation();
              if (action.onClick) action.onClick();
            }}
          >
            {action.icon && (
              <action.icon
                className={classNames(
                  'size-4',
                  action.key === FileActionKey.DELETE
                    ? 'text-text-error'
                    : 'text-fg-quaternary'
                )}
              />
            )}
            <span
              className={cn(
                'txt-small-medium',
                action.key === FileActionKey.DELETE
                  ? 'text-text-error'
                  : 'text-text-secondary'
              )}
            >
              {action.label}
            </span>
          </div>
        ))}
      </div>
    </PopoverContent>
    <PopoverTrigger asChild>
      <Button
        variant="tertiary"
        className="size-8 p-0"
        type="button"
        aria-label="File actions"
        onClick={(e) => e.stopPropagation()}
      >
        <Icons.DotsVertical className="size-4 shrink-0" aria-hidden="true" />
      </Button>
    </PopoverTrigger>
  </Popover>
);

const SingleFileUploader = ({
  file,
  accept,
  _className = '',
  maxSize,
  onFileUpload,
  getSignedUrlForPut,
  error,
  setError,
  setFile,
  documentType = LegacyDocumentType.Other,
  onFileChange,
  onError,
  token,
  disabled = false,
  onSuccess,
  onUploadStatusChange,
  message,
  fileActions,
  hideDefaultActions = [],
  companyId,
  clerkToken,
  id,
}: SingleFileUploaderProps) => {
  const [uploadStatus, setUploadStatus] = useState<FileUploadStatus | null>(
    null
  );

  const [localFile, setLocalFile] = useState<TLocalFile | null>(null);

  const [fileSize, setFileSize] = useState<number | null>(null);

  const [signedURL, setSignedURL] = useState<string>('');

  const viewDocumentModalVisibility = useToggle();

  const onRemoveFile = () => {
    setLocalFile(null);
    setFileSize(null);
    setFile(null);
    setUploadStatus(null);
    setError?.('');
  };

  // Resolve signedURL for preview/download
  // Priority: 1) file.signedUrl (already available), 2) file.id (fetch via authorized endpoint)
  useEffect(() => {
    const getSignedUrl = async () => {
      // If file already has a signedUrl, use it
      if (file?.signedUrl) {
        setSignedURL(file.signedUrl);
        return;
      }

      // Don't fetch if we already have a signedURL (e.g., from recent upload)
      if (signedURL) {
        return;
      }

      // If file has an id, fetch signed URL via authorized endpoint
      if (file?.id) {
        const fetchedSignedUrl = await getSignedUrlByFileId(
          file.id,
          clerkToken
        );
        if (fetchedSignedUrl) {
          setSignedURL(fetchedSignedUrl);
        }
      }
      // Note: If neither signedUrl nor id is available, preview will not be available.
      // This is intentional - parent must provide signedUrl or id for secure file access.
    };

    getSignedUrl();
  }, [file?.signedUrl, file?.id, clerkToken]);

  const handleDownloadFile = () => {
    if (signedURL && file?.fileName) {
      showNotification({
        valid: true,
        title: 'Downloading file...',
        message: 'Your file will be ready in a few seconds',
      });

      downloadFileFromLink(signedURL, file.fileName);

      return;
    }

    showNotification({
      valid: false,
      title: 'Download failed',
      message: 'Please try again',
    });
  };

  const getFileActionMenuItems = useCallback(() => {
    // If custom actions provided, use only those
    if (fileActions?.length) {
      return fileActions.sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    // Otherwise use default actions with standard behaviors
    return DEFAULT_ACTIONS.filter(
      (action) => !hideDefaultActions?.includes(action.key)
    ).map((action) => ({
      ...action,
      onClick: () => {
        switch (action.key) {
          case FileActionKey.DELETE:
            onRemoveFile();
            break;
          case FileActionKey.DOWNLOAD:
            handleDownloadFile();
            break;
          case FileActionKey.PREVIEW:
            if (file?.fileUrl) {
              viewDocumentModalVisibility.show();
            }
            break;
          default:
            break;
        }
      },
    }));
  }, [file, fileActions, onRemoveFile, viewDocumentModalVisibility]);

  useEffect(() => {
    if (file) {
      setLocalFile({
        fileName: file.fileName,
        fileSize: null,
        error: { hasError: !!error, message: error || null },
      });
    } else {
      setLocalFile(null);
      setFileSize(null);
      setUploadStatus(null);
    }
  }, [file, error]);

  useEffect(() => {
    onUploadStatusChange?.(uploadStatus);
  }, [uploadStatus, onUploadStatusChange]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setUploadStatus(FileUploadStatus.UPLOADING);

      if (rejectedFiles.length) {
        const rejection = rejectedFiles[0];
        const rejectedFile = rejection.file;
        const errorCode = rejection.errors[0].code as ErrorCode;
        const errorMessage = getErrorMessage(
          errorCode,
          rejection.errors?.[0]?.message || ''
        );

        setLocalFile({
          fileName: rejectedFile.name,
          fileSize: rejectedFile.size,
          error: {
            hasError: true,
            message: errorMessage,
          },
        });
        setFileSize(rejectedFile.size);
        setUploadStatus(FileUploadStatus.FAILED);
        return;
      }

      const uploadedFile = acceptedFiles[0];

      setLocalFile({
        fileName: uploadedFile.name,
        fileSize: uploadedFile.size,
        error: { hasError: false, message: null },
      });
      setFileSize(uploadedFile.size);

      const uploadFileResponse = onFileUpload
        ? await onFileUpload(uploadedFile, companyId, token, clerkToken)
        : await uploadFileToS3(
            uploadedFile,
            companyId,
            token,
            clerkToken,
            getSignedUrlForPut
          );

      if (uploadFileResponse?.downloadUrl) {
        setSignedURL(uploadFileResponse.downloadUrl);
      }

      if (!uploadFileResponse) {
        if (setError) setError('Something went wrong while uploading file');
        setUploadStatus(FileUploadStatus.FAILED);
        return;
      }

      const uploadedFileBasic: FileBasic = {
        fileName: uploadFileResponse.fileName,
        fileUrl: uploadFileResponse.fileUrl,
        documentType,
        signedUrl: uploadFileResponse.downloadUrl,
      };
      setFile(uploadedFileBasic);
      onFileChange?.(uploadedFileBasic);
      setUploadStatus(FileUploadStatus.SUCCESS);
      if (onSuccess && uploadFileResponse.fileUrl) onSuccess();
    },
    [setFile, onFileChange, file, documentType, onError, getSignedUrlForPut]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    ...(accept ? { accept } : {}),
    ...(maxSize ? { maxSize } : {}),
    multiple: false,
    disabled,
  });

  const getFileIcon = (fileName: string) => {
    const extension = Object.keys(fileIconMap).find((ext) =>
      fileName.endsWith(ext)
    );
    return extension ? fileIconMap[extension] : FileIcon;
  };

  const FileIconComponent: React.ElementType = getFileIcon(
    localFile?.fileName || ''
  );

  const renderFileActionsMenu = () => {
    const actions = getFileActionMenuItems();
    if (actions.length === 0) return null;

    return <DefaultActionsMenu actions={actions} />;
  };

  const fileUploaderContent = localFile ? (
    <div
      className={cn(
        'flex justify-between gap-xs p-4 border bg-bg-primary rounded-xl',
        _className,
        error || localFile?.error?.hasError ? 'items-start' : 'items-center'
      )}
    >
      <input {...getInputProps({ id })} />
      <div className="flex break-all items-center gap-lg flex-1">
        {FileIconComponent ? (
          <FileIconComponent className="size-8 shrink-0" />
        ) : (
          <FileIcon className="size-10 shrink-0" />
        )}

        <div className="flex flex-col gap-xs">
          <div className="flex flex-col">
            <span className="txt-small-medium text-text-secondary text-ellipsis">
              {localFile.fileName}
            </span>
            <span className="txt-small-regular text-text-tertiary">
              {uploadStatus !== FileUploadStatus.UPLOADING &&
              (error || localFile?.error?.hasError)
                ? localFile?.error?.message || 'Upload failed, please try again'
                : fileSize
                  ? formatBytesString(fileSize)
                  : ''}
            </span>
          </div>
          {uploadStatus !== FileUploadStatus.UPLOADING &&
          (error || localFile?.error?.hasError) ? (
            <span
              className="txt-small-bold text-button-tertiary-fg cursor-pointer"
              onClick={open}
            >
              Try again
            </span>
          ) : null}
        </div>
      </div>
      {uploadStatus === FileUploadStatus.UPLOADING ? (
        <LoadingIndicator _className="h-5 w-5" />
      ) : null}
      {localFile && uploadStatus !== FileUploadStatus.UPLOADING
        ? renderFileActionsMenu()
        : null}
    </div>
  ) : (
    <div
      className={cn(
        'flex items-center gap-lg p-xl rounded-xl border-dashed box-border cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
        _className,
        isDragActive
          ? 'border border-border-primary'
          : 'border border-border-primary',
        disabled
          ? 'bg-bg-disabled-subtle border-border-disabled cursor-not-allowed select-none'
          : 'bg-bg-primary border-border-primary hover:border-opacity-100 transition-all duration-100',
        error ? 'border-border-error hover:border-border-error' : ''
      )}
      {...getRootProps()}
    >
      <input {...getInputProps({ id })} />
      <FeaturedIcon icon={<Icons.Upload01 />} size="md" variant="gray-modern" />
      <div className="flex flex-col gap-xs">
        <div className="flex items-center gap-xs">
          <span className="txt-small-medium text-button-link-text underline underline-offset-2">
            Click to upload
          </span>
          <span className="txt-small-regular text-text-secondary">
            or drag and drop
          </span>
        </div>

        {message && (
          <span className="txt-extra-small-regular text-text-tertiary">
            {message}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <>
      {fileUploaderContent}
      {viewDocumentModalVisibility.visible && (
        <PreviewDownloadDialog
          isOpen={viewDocumentModalVisibility.visible}
          onClose={viewDocumentModalVisibility.hide}
          previewFile={{
            fileName: file?.fileName,
            signedURL,
          }}
        />
      )}
    </>
  );
};

export default SingleFileUploader;
